import { useContent } from "../App";
import { Button } from "../components/ui/button";
import { Phone, Mail, MapPin, Clock, Navigation, MessageCircle } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

export default function ContactPage() {
  const { content } = useContent();

  usePageSEO({
    title: "Contact Ambe Palace | Best Hotel in Dungarpur - Phone, Address & Directions",
    description: "Contact Ambe Palace, the best hotel in Dungarpur, Rajasthan. Call +91 6376696360, email contact@ambepalace.com. Located at Sagwara Road, Tijwaar, Dungarpur 314001."
  });

  const contact = content.contact || {
    phone: "6376696360",
    email: "contact@ambepalace.com",
    address: "Dungarpur, Sagwara Road, Tijwaar, Rajasthan 314001",
    directions_url: "https://share.google/mvDbvq07DpUPnSWH3",
    hours: { reception: "24/7", restaurant: "10 AM \u2013 12 AM", checkin: "10 AM", checkout: "12 AM" }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">Get in Touch</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact Ambe Palace {'\u2014'} Hotel in Dungarpur
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Near Me Location Signal */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-5 mb-8" data-testid="near-me-section">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <h2 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Find the Best Hotel Near You in Dungarpur</h2>
                <p className="text-sm text-slate-600">
                  Located in the heart of Dungarpur, Rajasthan — Ambe Palace is easily findable when you search "hotel near me" in or around Dungarpur. We are situated on Sagwara Road, Tijwaar, with convenient access from all major routes. Whether you are a traveler passing through or visiting Dungarpur, we are the top-rated hotel choice in this area.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Details */}
            <div className="space-y-6">
              <div className="bg-white border border-stone-100 p-6" data-testid="contact-details">
                <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Contact Details</h2>
                <div className="space-y-5">
                  <a href={`tel:+91${contact.phone}`} className="flex items-start gap-4 group" data-testid="contact-phone">
                    <div className="w-10 h-10 bg-amber-50 flex items-center justify-center shrink-0 group-hover:bg-amber-600 transition-colors duration-200">
                      <Phone className="w-4 h-4 text-amber-600 group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</p>
                      <p className="text-sm font-semibold text-slate-800">{contact.phone}</p>
                    </div>
                  </a>
                  <a href={`mailto:${contact.email}`} className="flex items-start gap-4 group" data-testid="contact-email">
                    <div className="w-10 h-10 bg-amber-50 flex items-center justify-center shrink-0 group-hover:bg-amber-600 transition-colors duration-200">
                      <Mail className="w-4 h-4 text-amber-600 group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Email</p>
                      <p className="text-sm font-semibold text-slate-800">{contact.email}</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4" data-testid="contact-address">
                    <div className="w-10 h-10 bg-amber-50 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Address</p>
                      <p className="text-sm font-semibold text-slate-800">{contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a href={contact.directions_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none h-11 transition-colors duration-200"
                      data-testid="get-direction-btn"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Get Direction
                    </Button>
                  </a>
                  <a href={`https://wa.me/91${contact.phone}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none h-11 transition-colors duration-200"
                      data-testid="whatsapp-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="space-y-6">
              <div className="bg-white border border-stone-100 p-6" data-testid="opening-hours">
                <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Opening Hours</h2>
                <div className="space-y-4">
                  {[
                    { label: "Reception", value: contact.hours?.reception || "24/7", icon: Clock },
                    { label: "Restaurant", value: contact.hours?.restaurant || "10 AM \u2013 12 AM", icon: Clock },
                    { label: "Check-in", value: contact.hours?.checkin || "10 AM", icon: Clock },
                    { label: "Check-out", value: contact.hours?.checkout || "12 AM", icon: Clock },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-stone-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-slate-700">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6">
                <h3 className="text-sm font-bold text-slate-900 mb-2">Need Help?</h3>
                <p className="text-sm text-slate-600 mb-3">
                  For any queries about rooms, bookings, or dining, feel free to reach out to us anytime.
                </p>
                <a href={`tel:+91${contact.phone}`} className="text-amber-600 font-bold text-sm hover:text-amber-700 transition-colors duration-200">
                  Call Now: {contact.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
